import { Chip, CustomButton } from '@/shared/ui/components';
import { AppPaired, HighSamplingRate, isEndOfLife } from '@/entities/device/@x/study';
import { ExternalLinkIcon } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import {
  getDuration,
  zeroPad,
  checkToFormatPhoneNumber,
  formatDiagnosisCode,
  formatPhoneNumber,
  generateFullName,
  dateToString,
  dateToStringWithDailyTimeZone,
  formatInitialDuration,
  getStudyDuration,
  gettextStudyStatus,
} from '@/shared/lib';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { checkUsingHighSamplingRateStudy } from './checkUsingHighSamplingRateStudy';
import { formatDeviceBiodirect } from './formatDeviceBiodirect';
import { shouldDisplayStudyStartTime } from './shouldDisplayStudyStartTime';
import type { LinkedStudy, StudyInfo } from '../ui/studyInfoDrawer/studyInfoDrawer.types';
import { STUDY_STATUS } from './studyConstants';

dayjs.extend(utc);

/**
 * Renders the badge for one leg of a follow-on study pair in the study info drawer. Shows a grey 'Not started' chip for a missing linked study, an orange 'Cancelled' chip when that leg's follow-on plan was cancelled, a blue 'This study' chip when the leg's friendlyId matches the drawer's study, and otherwise an Open button that launches `/studies/{id}` in a new window.
 *
 * @param friendlyId - Friendly id of the study the drawer is showing.
 * @param linkedStudy - Follow-on leg being rendered, possibly empty.
 * @param cancelledReason - Reason the follow-on study was cancelled, absent while the plan is still live.
 * @returns Badge markup for that leg.
 */
const renderFoList = (
  friendlyId: number,
  linkedStudy: LinkedStudy | undefined,
  cancelledReason?: string,
) => {
  if (_.isEmpty(linkedStudy)) {
    const { background, color, label } = cancelledReason
      ? { background: '#FFF4E5', color: '#F58F0A', label: t`Cancelled` }
      : { background: '#EAEDF1', color: '#444F66', label: t`Not started` };
    return (
      <div className="badge">
        <Chip background={background} color={color} closable={false} className="custom-chip">
          {label}
        </Chip>
      </div>
    );
  }

  if (friendlyId === linkedStudy.friendlyId) {
    return (
      <div className="badge">
        <Chip background="#D9E7FA" color="#2265C6" closable={false} className="custom-chip">
          {t`This study`}
        </Chip>
      </div>
    );
  }

  return (
    <div className="badge">
      <CustomButton
        id="open-btn"
        style="default"
        type="secondary"
        size="small"
        isRightIcon
        icon={<ReactSVG src={ExternalLinkIcon} />}
        onClick={() => {
          window.open(`/studies/${linkedStudy.id}`);
        }}
      >
        {t`Open`}
      </CustomButton>
    </div>
  );
};

/**
 * Builds the two follow-on tracking rows for the study info drawer, one per leg of the pair. Reads each leg from linkedStudies when present and otherwise falls back to the study itself and its info.followOnStudy, showing zero-padded study ids and types for a linked pair or the planned duration and type for a not-yet-started leg, each row closed by its badge.
 *
 * @param study - Study whose linked or planned follow-on legs are rendered.
 * @returns Markup for the two follow-on rows.
 */
const foStudyContent = (study: StudyInfo) => {
  const { linkedStudies, friendlyId } = study;
  const firstStudy = linkedStudies?.length > 0 ? linkedStudies[0] : study;
  const secondStudy = linkedStudies?.length > 0 ? linkedStudies[1] : undefined;
  return [
    <div className="study">
      <div>
        <div className="item">
          <div className="label">{`${t`Study ID`}:`}</div>
          <div className="value">
            {study.linkedStudies?.length > 0
              ? zeroPad(study.linkedStudies[0].friendlyId)
              : zeroPad(study.friendlyId)}
          </div>
        </div>
        <div className="item">
          <div className="label">{`${t`Study type`}:`}</div>
          <div className="value">
            {study.linkedStudies?.length > 0 ? study.linkedStudies[0].studyType : study.studyType}
          </div>
        </div>
      </div>
      {renderFoList(friendlyId, firstStudy)}
    </div>,
    <div className="study">
      <div>
        <div className="item">
          <div className="label">
            {`${study.linkedStudies?.length > 0 ? t`Study ID` : t`Study duration`}:`}
          </div>
          <div className="value">
            {study.linkedStudies?.length > 0
              ? zeroPad(study.linkedStudies[1].friendlyId)
              : getDuration(study.info?.followOnStudy?.duration, 'd[d]', 'h')}
          </div>
        </div>
        <div className="item">
          <div className="label">{`${t`Study type`}:`}</div>
          <div className="value">
            {study.linkedStudies?.length > 0
              ? study.linkedStudies[1].studyType
              : study.info?.followOnStudy?.studyType}
          </div>
        </div>
      </div>
      {renderFoList(friendlyId, secondStudy, study.info?.followOnStudy?.cancelledReason)}
    </div>,
  ];
};

/**
 * Assembles every labelled section the study info drawer displays for one study. Produces study information (status, initiated, start and end times in the study's daily timezone, type, duration, return visit, device id decorated with high-sampling-rate, app-paired and EOL markers, clinician initials), an optional follow-on section, referring and interpreting physician blocks with phones formatted only where needed, and diagnosis and amendment notes, using a double-hyphen bullet for missing values.
 *
 * @param study - Study whose details are laid out.
 * @param isOngoing - Whether the drawer was opened from the ongoing studies tab.
 * @param isFlaggedStudiesTab - Whether the drawer was opened from the flagged studies tab.
 * @returns Section descriptors for the drawer, with holes where a section does not apply.
 */
const formatDataStudy = (
  study: StudyInfo,
  {
    isOngoing,
    isFlaggedStudiesTab,
  }: {
    isOngoing: boolean;
    isFlaggedStudiesTab: boolean;
  },
) => {
  const shouldFormatReferringPhysicianPhoneNumber = checkToFormatPhoneNumber(
    study.info?.referringPhysician?.contact?.country,
  );
  const shouldFormatInterpretingPhysicianPhoneNumber = checkToFormatPhoneNumber(
    study.info?.interprettingPhysician?.contact?.country,
  );
  return [
    {
      header: t`Study information`,
      content: [
        {
          label: t`Status`,
          value:
            study.device?.deviceId === '0000000000' && study.status === STUDY_STATUS.DRAFT
              ? t`Not started`
              : gettextStudyStatus(study.status),
        },
        {
          label: t`Initiated time`,
          value: study.initiatedTime
            ? dateToStringWithDailyTimeZone({
                startStudyDate: study.start,
                isoDate: study.initiatedTime,
                timezone: study.timezone,
                studyTimeOffset: study.studyTimeOffset ?? undefined,
                facility: study.facility,
              })
            : DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Start time`,
          value: shouldDisplayStudyStartTime(study.status)
            ? study.start
              ? dateToStringWithDailyTimeZone({
                  startStudyDate: study.start,
                  isoDate: study.start,
                  timezone: study.timezone,
                  studyTimeOffset: study.studyTimeOffset ?? undefined,
                  facility: study.facility,
                })
              : DOUBLE_HYPHEN_BULLET
            : DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`End time`,
          value: study.stop
            ? dateToStringWithDailyTimeZone({
                startStudyDate: study.start,
                isoDate: study.stop,
                timezone: study.timezone,
                studyTimeOffset: study.studyTimeOffset ?? undefined,
                facility: study.facility,
              })
            : DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Study type`,
          value: study.studyType || DOUBLE_HYPHEN_BULLET,
          linkedStudies: study.linkedStudies,
          followOnStudy: study.info?.followOnStudy,
        },
        {
          label: t`Study duration`,
          value: [STUDY_STATUS.DRAFT, STUDY_STATUS.STARTING].includes(study.status)
            ? getDuration(study.initDuration, 'd[d] hh[h] mm[m]', 'h')
            : getDuration(getStudyDuration(study.start, study.stop), 'd[d] hh[h] mm[m]', 'h'),
        },
        {
          label: t`Initial duration`,
          value: formatInitialDuration(study.initialDurationOfStudy) || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Patient return visit`,
          value:
            (isOngoing || isFlaggedStudiesTab) && !_.isEmpty(study.info?.followOnStudy)
              ? dateToString({
                  isoDate: dayjs.utc(study.info?.followOnStudy.reportDueDate, 'YYYY-MM-DD'),
                  timezone: 'UTC+0',
                  format: 'MM/DD/YYYY',
                })
              : study.reportDueDate
                ? dateToString({
                    isoDate: study.reportDueDate,
                    timezone: 'UTC+0',
                    format: 'MM/DD/YYYY',
                  })
                : DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Device ID`,
          value: (
            <div className="device-id-row">
              {formatDeviceBiodirect(study.device?.deviceId)}
              {checkUsingHighSamplingRateStudy(study.ecgDetail) && (
                <HighSamplingRate className="ml2" />
              )}
              {!!study.patientFirstSyncDeviceAt && <AppPaired />}
              {isEndOfLife(study.device?.firstStudyDate) && (
                <Chip
                  style={{ border: 1, paddingInline: 8 }}
                  background="#FFEBEB"
                  color="#BD1319"
                  closable={false}
                >
                  {t`EOL`}
                </Chip>
              )}
            </div>
          ),
        },
        {
          label: t`Clinician initials`,
          value: study.info.clinicianInitials || DOUBLE_HYPHEN_BULLET,
        },
      ],
    },
    study.linkedStudies?.length > 0 || study.info?.followOnStudy
      ? {
          header: t`Follow-on study tracking`,
          content: foStudyContent(study),
          className: 'drawer-study-tracking',
          wrapperClassName: 'fo-study',
        }
      : undefined,
    {
      header: t`Referring physician`,
      content: [
        {
          label: t`Name`,
          value: generateFullName(study.info?.referringPhysician),
        },
        {
          label: t`Day phone`,
          value:
            (shouldFormatReferringPhysicianPhoneNumber
              ? formatPhoneNumber(study.info?.referringPhysician?.contact?.phone1)
              : study.info?.referringPhysician?.contact?.phone1) || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`After hours phone`,
          value:
            (shouldFormatReferringPhysicianPhoneNumber
              ? formatPhoneNumber(study.info?.referringPhysician?.contact?.nightPhone)
              : study.info?.referringPhysician?.contact?.nightPhone) || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Email`,
          value: study.info?.referringPhysician?.email || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Fax number`,
          value: study.info?.referringPhysician?.contact?.fax || DOUBLE_HYPHEN_BULLET,
        },
      ],
    },
    {
      header: t`Interpreting physician`,
      content: [
        {
          label: t`Name`,
          value: generateFullName(study.info?.interprettingPhysician),
        },
        {
          label: t`Day phone`,
          value:
            (shouldFormatInterpretingPhysicianPhoneNumber
              ? formatPhoneNumber(study.info?.interprettingPhysician?.contact?.phone1)
              : study.info?.interprettingPhysician?.contact?.phone1) || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`After hours phone`,
          value:
            (shouldFormatInterpretingPhysicianPhoneNumber
              ? formatPhoneNumber(study.info?.interprettingPhysician?.contact?.nightPhone)
              : study.info?.interprettingPhysician?.contact?.nightPhone) || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Email`,
          value: study.info?.interprettingPhysician?.email || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Fax number`,
          value: study.info?.interprettingPhysician?.contact?.fax || DOUBLE_HYPHEN_BULLET,
        },
      ],
    },
    {
      header: t`Diagnosis`,
      content: [
        {
          label: t`Primary diagnosis code`,
          value: formatDiagnosisCode(study.info?.diagnosis, true),
        },
        study.info?.diagnosis?.second
          ? {
              label: t`Secondary diagnosis code`,
              value: formatDiagnosisCode(study.info?.diagnosis),
            }
          : undefined,
        {
          label: t`Patient medication`,
          value: study.info?.diagnosis?.patientMedications || DOUBLE_HYPHEN_BULLET,
        },
      ],
    },
    {
      header: t`Physician amendment notes`,
      content: study.info?.diagnosis?.physiciansNote || DOUBLE_HYPHEN_BULLET,
    },
  ];
};

export { foStudyContent, formatDataStudy };
